<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useQuery } from '@vue/apollo-composable'
import {
  ImageOptions,
  StepChangeField,
  ViewStepDataQuery,
  ViewStepDataQueryVariables,
} from '@/generated/graphql'
import gql from 'graphql-tag'
import { computed, watch } from 'vue'
import IconArticleType from '@/components/icons/IconArticleType.vue'
import ArticleName from '@/components/article/ArticleName.vue'
import Markdown from '@/components/Markdown.vue'
import StepUploadsViewer from '@/components/stepUpload/StepUploadsViewer.vue'
import ArticleQuantity from '@/components/article/ArticleQuantity.vue'
import { useStepArticleColors } from '@/composables/useStepArticleColors'
import OperatorFontSizeSelector from '@/components/operator/OperatorFontSizeSelector.vue'
import StepOpenIssuesButton from '@/components/step/StepOpenIssuesButton.vue'
import StepIconList from '@/components/icon/StepIconList.vue'
import { CreateIssueSeed } from '@/components/issue/CreateIssueDialog.vue'

export type StepDraft = {
  name?: string
  stepTypeName?: string
  instructionText?: string
}

const props = defineProps<{
  stepId: string
  bopId?: string
  hideFontSize?: boolean
  graphNodeStepId?: string
  hideAllocations?: boolean
  hideInputs?: boolean
  hideTools?: boolean
  hideTitle?: boolean
  draft?: StepDraft
  highlightedFields?: StepChangeField[]
  highlightColor?: 'error' | 'success' | 'warning'
}>()

type LocalPartAllocation = ViewStepDataQuery['product']['step']['articleAllocations'][0]
type LocalStepInput = ViewStepDataQuery['product']['step']['inputs'][0]

const { t } = useI18n()

const isHighlighted = (field: StepChangeField) => props.highlightedFields?.includes(field) ?? false
const highlightClass = (field: StepChangeField) => {
  if (!isHighlighted(field)) return undefined
  return `field-highlight field-highlight--${props.highlightColor ?? 'warning'}`
}

// Shared per-step colours so these swatches match the 3D viewer (and each other).
const { swatchColor } = useStepArticleColors(
  computed(() => props.stepId),
  computed(() => props.graphNodeStepId),
)

const imageOptions: ImageOptions = {
  height: 500,
  width: 500,
}
const fetchQuery = useQuery<ViewStepDataQuery, ViewStepDataQueryVariables>(
  gql`
    query ViewStepData($id: ID!, $imageOptions: ImageOptions!) {
      product {
        step(id: $id) {
          id
          instructionText
          warningText
          name
          stepType {
            id
            name
          }
          module {
            id
          }
          issueLinks {
            ... on IssueLinkI {
              id
              issue {
                id
                name
                createdAt
                resolvedAt
                assignedToUsers {
                  id
                  firstname
                  lastname
                }
                labels {
                  id
                  name
                  color
                }
              }
            }
          }
          icons {
            id
            name
            imageUpload {
              id
              imageUrl(options: { width: 96, height: 96 })
            }
          }
          toolIds
          tools {
            id
            name
            description
            imageUploads {
              id
              imageUrl(options: $imageOptions)
            }
          }
          articleAllocations {
            ... on IArticleAllocation {
              id
              milliQuantity
              article {
                id
                type
                name
                unit
                revision
                articleNumber
              }
            }
          }
          uploads {
            ... on IStepUpload {
              id
            }
          }
          inputs {
            id
            milliQuantity
            output {
              id
              milliQuantity
              module {
                id
                name
              }
              step {
                id
                name
              }
              article {
                id
                name
                articleNumber
                revision
                unit
              }
            }
          }
        }
      }
    }
  `,
  () => ({
    id: props.stepId,
    imageOptions,
  }),
)
const step = computed(() => fetchQuery.result.value?.product.step)

const emit = defineEmits<{ loaded: [] }>()
watch(
  step,
  (s) => {
    if (s) emit('loaded')
  },
  { immediate: true },
)

const placementQuery = useQuery(
  gql`
    query ViewStepPlacementData($graphNodeStepId: ID!, $imageOptions: ImageOptions!) {
      product {
        placementArticleAllocations(graphNodeStepId: $graphNodeStepId) {
          ... on IArticleAllocation {
            id
            milliQuantity
            article {
              id
              type
              name
              unit
              revision
              articleNumber
            }
          }
        }
        placementStepInputs(graphNodeStepId: $graphNodeStepId) {
          id
          milliQuantity
          output {
            id
            milliQuantity
            module {
              id
              name
            }
            step {
              id
              name
            }
            article {
              id
              name
              articleNumber
              revision
              unit
            }
          }
        }
        placementTools(graphNodeStepId: $graphNodeStepId) {
          id
          name
          description
          imageUploads {
            id
            imageUrl(options: $imageOptions)
          }
        }
      }
    }
  `,
  () => ({ graphNodeStepId: props.graphNodeStepId ?? '', imageOptions }),
  () => ({ enabled: !!props.graphNodeStepId }),
)
type LocalTool = ViewStepDataQuery['product']['step']['tools'][0]
type PlacementResult = {
  product?: {
    placementArticleAllocations?: LocalPartAllocation[]
    placementStepInputs?: LocalStepInput[]
    placementTools?: LocalTool[]
  }
}
const articleAllocations = computed<LocalPartAllocation[]>(() =>
  props.graphNodeStepId
    ? ((placementQuery.result.value as PlacementResult | undefined)?.product
        ?.placementArticleAllocations ?? [])
    : (step.value?.articleAllocations as LocalPartAllocation[]) || [],
)
const mergedArticleAllocations = computed(() => {
  const map = new Map<string, LocalPartAllocation>()
  for (const a of articleAllocations.value) {
    const existing = map.get(a.article.id)
    if (existing) {
      map.set(a.article.id, {
        ...existing,
        milliQuantity: existing.milliQuantity + a.milliQuantity,
      })
    } else {
      map.set(a.article.id, { ...a })
    }
  }
  return Array.from(map.values())
})
const inputs = computed<LocalStepInput[]>(() =>
  props.graphNodeStepId
    ? ((placementQuery.result.value as PlacementResult | undefined)?.product?.placementStepInputs ??
      [])
    : step.value?.inputs || [],
)
const tools = computed<LocalTool[]>(() =>
  props.graphNodeStepId
    ? ((placementQuery.result.value as PlacementResult | undefined)?.product?.placementTools ?? [])
    : step.value?.tools || [],
)
const hasUploads = computed(() => (step.value?.uploads?.length ?? 0) > 0)
const icons = computed(() => step.value?.icons ?? [])
const openIssueLinks = computed(() =>
  (step.value?.issueLinks ?? []).filter((l) => l.issue && !l.issue.resolvedAt),
)
const displayName = computed(() => props.draft?.name ?? step.value?.name)
const displayStepTypeName = computed(() => props.draft?.stepTypeName ?? step.value?.stepType.name)
const displayInstructionText = computed(
  () => props.draft?.instructionText ?? step.value?.instructionText,
)
const createIssueSeed = computed<CreateIssueSeed | undefined>(() => {
  const bopId = props.bopId
  const moduleId = step.value?.module?.id
  if (!bopId || !moduleId) return undefined
  return { bopId, moduleId, stepId: props.stepId }
})
</script>

<template>
  <template v-if="!step">
    <v-skeleton-loader type="article" />
    <v-skeleton-loader type="card" />
    <v-skeleton-loader type="article" />
  </template>

  <template v-if="step">
    <div v-if="!hideTitle" :class="highlightClass(StepChangeField.Name)">
      <div class="step-title-row">
        <h1 class="step-title">
          {{ displayName }}
          <v-chip
            :color="
              isHighlighted(StepChangeField.StepType) ? (highlightColor ?? 'warning') : undefined
            "
          >
            {{ displayStepTypeName }}
          </v-chip>
          <step-open-issues-button :links="openIssueLinks" :create-seed="createIssueSeed" />
        </h1>
        <div
          v-if="icons.length > 0"
          :class="['step-title-icons', highlightClass(StepChangeField.Icons)]"
        >
          <step-icon-list :icons="icons" />
        </div>
        <div v-if="$slots.titleActions" class="step-title-actions">
          <slot name="titleActions" />
        </div>
      </div>
    </div>

    <div
      v-if="step.warningText"
      :class="['step-warning', highlightClass(StepChangeField.WarningText)]"
      role="alert"
    >
      <v-icon icon="warning" class="step-warning-icon" size="x-large" />
      <strong class="step-warning-text">{{ step.warningText }}</strong>
    </div>

    <v-row>
      <v-col>
        <v-expansion-panels
          v-if="!hideInputs && inputs.length > 0"
          variant="accordion"
          elevation="1"
          :model-value="[0]"
          :class="['section-panel', highlightClass(StepChangeField.Inputs)]"
        >
          <v-expansion-panel>
            <v-expansion-panel-title class="section-panel-title">
              {{ t('entity.stepInput.plural') }}
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <v-table density="comfortable">
                <thead>
                  <tr>
                    <td style="width: 12px" />
                    <td>{{ t('entity.module.singular') }} & {{ t('entity.step.singular') }}</td>
                    <td>{{ t('entity.article.singular') }}</td>
                    <td>{{ t('entity.stepInput.field.milliQuantity') }}</td>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in inputs" :key="item.id">
                    <td>
                      <span
                        class="color-dot color-dot--md"
                        :style="{ background: swatchColor(item.output.article.articleNumber) }"
                      />
                    </td>
                    <td>
                      <strong>{{ item.output.module?.name }}</strong> <br />{{
                        item.output.step.name
                      }}
                    </td>
                    <td><article-name :article="item.output.article" /></td>
                    <td>
                      <article-quantity
                        :milli-quantity="item.milliQuantity"
                        :unit="item.output.article.unit"
                        unit-abbreviated
                      />
                    </td>
                  </tr>
                </tbody>
              </v-table>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>

        <v-expansion-panels
          v-if="!hideAllocations && mergedArticleAllocations.length > 0"
          variant="accordion"
          elevation="1"
          :model-value="[0]"
          :class="['section-panel', highlightClass(StepChangeField.ArticleAllocations)]"
        >
          <v-expansion-panel>
            <v-expansion-panel-title class="section-panel-title">
              {{ t('entity.article.plural') }}
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <v-table density="comfortable">
                <thead>
                  <tr>
                    <td style="width: 12px" />
                    <td>{{ t('entity.article.field.type') }}</td>
                    <td>{{ t('entity.article.singular') }}</td>
                    <td>{{ t('entity.article.field.quantity') }}</td>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="a in mergedArticleAllocations" :key="a.id">
                    <td>
                      <span
                        class="color-dot color-dot--md"
                        :style="{ background: swatchColor(a.article.articleNumber) }"
                      />
                    </td>
                    <td><icon-article-type :type="a.article.type" /></td>
                    <td><article-name :article="a.article" /></td>
                    <td>
                      <article-quantity
                        :milli-quantity="a.milliQuantity"
                        :unit="a.article.unit"
                        unit-abbreviated
                      />
                    </td>
                  </tr>
                </tbody>
              </v-table>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>

        <v-expansion-panels
          v-if="!hideTools && tools.length > 0"
          variant="accordion"
          elevation="1"
          :model-value="[0]"
          :class="['section-panel', highlightClass(StepChangeField.Tools)]"
        >
          <v-expansion-panel>
            <v-expansion-panel-title class="section-panel-title">
              {{ t('entity.step.field.tools') }}
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <v-table density="comfortable">
                <thead>
                  <tr>
                    <td>{{ t('entity.tool.field.name') }}</td>
                    <td>{{ t('entity.tool.field.description') }}</td>
                    <td>{{ t('entity.tool.field.images') }}</td>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="tool in tools" :key="tool.id">
                    <td class="font-weight-bold">{{ tool.name }}</td>
                    <td>{{ tool.description }}</td>
                    <td>
                      <div class="tool-thumbnails">
                        <v-img
                          v-for="u in tool.imageUploads"
                          :key="u.id"
                          :src="u.imageUrl"
                          class="tool-thumbnail"
                        />
                      </div>
                    </td>
                  </tr>
                </tbody>
              </v-table>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>

        <div
          v-if="!!displayInstructionText"
          :class="highlightClass(StepChangeField.InstructionText)"
        >
          <div class="instruction-header">
            <h2>{{ t('entity.step.field.instructionText') }}</h2>
            <operator-font-size-selector v-if="!hideFontSize" />
          </div>
          <markdown :text="displayInstructionText" />
        </div>
      </v-col>

      <v-col v-if="hasUploads" cols="6">
        <div :class="highlightClass(StepChangeField.Uploads)">
          <step-uploads-viewer :step-id="stepId" :max-image-height="500" :max-image-width="500" />
        </div>
      </v-col>
    </v-row>
  </template>
</template>

<style scoped lang="scss">
.step-title-row {
  --step-title-font-size: 2.5rem;
  display: flex;
  align-items: flex-start;
  gap: 12px;

  .step-title {
    flex: 1;
    min-width: 0;
    font-size: var(--step-title-font-size);
  }

  .step-title-icons {
    --step-icon-size: var(--step-title-font-size);
    --step-title-icons-padding: calc(var(--step-title-font-size) * 0.25);
    display: flex;
    flex-shrink: 0;
    padding: var(--step-title-icons-padding);
    // Centers the icons on the title's first line, which is 1.425 times its font size
    margin-top: calc(var(--step-title-font-size) * 0.2125 - var(--step-title-icons-padding));
    border-radius: 6px;

    &.field-highlight {
      margin-bottom: 0;
      border-left: none;
    }

    &.field-highlight--error {
      box-shadow: inset 0 0 0 2px rgb(var(--v-theme-error));
    }

    &.field-highlight--success {
      box-shadow: inset 0 0 0 2px rgb(var(--v-theme-success));
    }

    &.field-highlight--warning {
      box-shadow: inset 0 0 0 2px rgb(var(--v-theme-warning));
    }
  }

  .step-title-actions {
    flex-shrink: 0;
    // Optically centered against the title's first line
    margin-top: 8px;
  }
}

.step-title {
  .v-chip,
  .v-badge {
    vertical-align: middle;
    margin-left: 8px;
  }
}
thead {
  font-weight: bold;
}
.section-panel {
  margin-top: 0.75em;

  &:first-of-type {
    margin-top: 0;
  }
}
.instruction-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 1.5em;

  h2 {
    margin: 0;
  }
}
.section-panel-title {
  min-height: 44px;
  padding-top: 6px;
  padding-bottom: 6px;
  font-weight: bold;
  font-size: 1.05rem;

  &.v-expansion-panel-title--active {
    min-height: 44px;
  }
}
.step-warning {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin: 0.75em 0 1em;
  padding: 12px 16px;
  border-radius: 4px;
  background: rgba(255, 193, 7, 0.15);
  border-left: 4px solid #ffc107;
}
.step-warning-icon {
  color: #ffc107;
  flex-shrink: 0;
}
.step-warning-text {
  white-space: pre-line;
  align-self: center;
}
.tool-thumbnails {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 4px 0;
}
.tool-thumbnail {
  width: 64px;
  height: 64px;
  border-radius: 4px;
}
.field-highlight {
  padding-left: 0.75em;
  border-radius: 0 4px 4px 0;
  margin-bottom: 0.5em;

  &--error {
    border-left: 3px solid rgb(var(--v-theme-error));
    background: rgba(var(--v-theme-error), 0.08);
  }

  &--success {
    border-left: 3px solid rgb(var(--v-theme-success));
    background: rgba(var(--v-theme-success), 0.1);
  }

  &--warning {
    border-left: 3px solid rgb(var(--v-theme-warning));
    background: rgba(var(--v-theme-warning), 0.1);
  }
}
</style>
